package com.trevorwiebe.timesheet.core.presentation

import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ExitToApp
import androidx.compose.material.icons.filled.DateRange
import androidx.compose.material.icons.filled.Settings
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.NavigationBar
import androidx.compose.material3.NavigationBarItem
import androidx.compose.material3.NavigationDrawerItem
import androidx.compose.material3.NavigationRail
import androidx.compose.material3.NavigationRailItem
import androidx.compose.material3.PermanentDrawerSheet
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.trevorwiebe.timesheet.Route
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource
import timesheet.composeapp.generated.resources.Res
import timesheet.composeapp.generated.resources.app_logo
import timesheet.composeapp.generated.resources.baseline_article_24
import timesheet.composeapp.generated.resources.baseline_payments_24
import timesheet.composeapp.generated.resources.baseline_receipt_long_24
import timesheet.composeapp.generated.resources.baseline_local_shipping_24
import timesheet.composeapp.generated.resources.baseline_more_horiz_24
import timesheet.composeapp.generated.resources.baseline_punch_clock_24
import timesheet.composeapp.generated.resources.baseline_speed_24

sealed class BottomNavItem(val route: Route, val icon: DrawableResource, val label: String) {
    data object Punch :
        BottomNavItem(Route.Punch, Res.drawable.baseline_punch_clock_24, "Clock I/O")
    data object Loads :
        BottomNavItem(Route.Loads, Res.drawable.baseline_local_shipping_24, "Loads")

    data object Mileage :
        BottomNavItem(Route.Mileage, Res.drawable.baseline_speed_24, "Mileage Log")

    data object More : BottomNavItem(Route.More, Res.drawable.baseline_more_horiz_24, "More")
}

val mainNavItems = listOf(
    BottomNavItem.Punch,
    BottomNavItem.Loads,
    BottomNavItem.Mileage,
    BottomNavItem.More,
)

val mainScreenRoutes = listOf(
    "com.trevorwiebe.timesheet.Route.Punch",
    "com.trevorwiebe.timesheet.Route.Loads",
    "com.trevorwiebe.timesheet.Route.Mileage",
    "com.trevorwiebe.timesheet.Route.More",
)

private val adminSectionRouteNames = setOf(
    "Admin", "Organization", "UserManagement", "Rates", "Equipment",
    "Locations", "Customers", "Carriers", "Reporting", "EmployeeHours", "EmployeePayPeriods",
)

fun isAdminSectionRoute(route: String?): Boolean {
    val name = route
        ?.removePrefix("com.trevorwiebe.timesheet.Route.")
        ?.substringBefore('/')
        ?: return false
    return name in adminSectionRouteNames
}

private fun isTabSelected(currentRoute: String?, item: BottomNavItem): Boolean =
    currentRoute?.substringAfterLast('.') == item.route.toString()

@Composable
private fun navItemColor(selected: Boolean): Color =
    if (selected) MaterialTheme.colorScheme.tertiary
    else MaterialTheme.colorScheme.onSurface

/** Bottom navigation bar shown on compact (phone-width) windows. */
@Composable
fun AppBottomBar(
    currentRoute: String?,
    onTabClick: (Route) -> Unit,
) {
    NavigationBar(
        containerColor = MaterialTheme.colorScheme.surface,
    ) {
        mainNavItems.forEach { item ->
            val selected = isTabSelected(currentRoute, item)
            NavigationBarItem(
                selected = selected,
                onClick = { onTabClick(item.route) },
                icon = {
                    Icon(
                        painter = painterResource(item.icon),
                        contentDescription = item.label,
                        tint = navItemColor(selected)
                    )
                },
                label = {
                    Text(
                        text = item.label,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        color = navItemColor(selected)
                    )
                }
            )
        }
    }
}

/**
 * Side navigation for medium+ windows: a [NavigationRail] when [expanded] is
 * false, a full [PermanentDrawerSheet] menu when true. Besides the main tabs it
 * carries the secondary actions (Time Off, Admin when the user is an admin)
 * and Sign Out pinned to the bottom.
 */
@Composable
fun AppSideNav(
    expanded: Boolean,
    currentRoute: String?,
    isAdmin: Boolean,
    onTabClick: (Route) -> Unit,
    onPayPeriodsClick: () -> Unit,
    onTimeOffClick: () -> Unit,
    onAdminClick: () -> Unit,
    onInvoicingClick: () -> Unit,
    onSettlementsClick: () -> Unit,
    onSignOutClick: () -> Unit,
) {
    val payPeriodsSelected = currentRoute == "com.trevorwiebe.timesheet.Route.PayPeriod"
    val timeOffSelected = currentRoute == "com.trevorwiebe.timesheet.Route.Calendar"
    val adminSelected = isAdminSectionRoute(currentRoute)
    val invoicingSelected = currentRoute == "com.trevorwiebe.timesheet.Route.Invoicing"
    val settlementsSelected = currentRoute == "com.trevorwiebe.timesheet.Route.Settlements"

    if (expanded) {
        PermanentDrawerSheet(
            modifier = Modifier.width(260.dp),
            drawerContainerColor = MaterialTheme.colorScheme.surface,
        ) {
            Column(
                modifier = Modifier
                    .fillMaxHeight()
                    .padding(horizontal = 12.dp)
            ) {
                Column(
                    modifier = Modifier.padding(horizontal = 8.dp, vertical = 20.dp),
                    horizontalAlignment = Alignment.Start,
                ) {
                    AppLogo()
                    Spacer(Modifier.height(8.dp))
                    Text(
                        text = "Santa Fe Carriers",
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.SemiBold,
                        fontSize = 24.sp
                    )
                }
                mainNavItems.forEach { item ->
                    val selected = isTabSelected(currentRoute, item)
                    SideNavDrawerItem(
                        label = item.label,
                        selected = selected,
                        onClick = { onTabClick(item.route) },
                        icon = {
                            Icon(
                                painter = painterResource(item.icon),
                                contentDescription = item.label,
                                tint = navItemColor(selected)
                            )
                        }
                    )
                }
                HorizontalDivider(Modifier.padding(vertical = 8.dp))
                SideNavDrawerItem(
                    label = "Pay Periods",
                    selected = payPeriodsSelected,
                    onClick = onPayPeriodsClick,
                    icon = {
                        Icon(
                            painter = painterResource(Res.drawable.baseline_article_24),
                            contentDescription = "Pay Periods",
                            tint = navItemColor(payPeriodsSelected)
                        )
                    }
                )
                SideNavDrawerItem(
                    label = "Time Off",
                    selected = timeOffSelected,
                    onClick = onTimeOffClick,
                    icon = {
                        Icon(
                            imageVector = Icons.Default.DateRange,
                            contentDescription = "Time Off",
                            tint = navItemColor(timeOffSelected)
                        )
                    }
                )
                if (isAdmin) {
                    SideNavDrawerItem(
                        label = "Invoicing",
                        selected = invoicingSelected,
                        onClick = onInvoicingClick,
                        icon = {
                            Icon(
                                painter = painterResource(Res.drawable.baseline_receipt_long_24),
                                contentDescription = "Invoicing",
                                tint = navItemColor(invoicingSelected)
                            )
                        }
                    )
                    SideNavDrawerItem(
                        label = "Settlements",
                        selected = settlementsSelected,
                        onClick = onSettlementsClick,
                        icon = {
                            Icon(
                                painter = painterResource(Res.drawable.baseline_payments_24),
                                contentDescription = "Settlements",
                                tint = navItemColor(settlementsSelected)
                            )
                        }
                    )
                    SideNavDrawerItem(
                        label = "Admin",
                        selected = adminSelected,
                        onClick = onAdminClick,
                        icon = {
                            Icon(
                                imageVector = Icons.Default.Settings,
                                contentDescription = "Admin",
                                tint = navItemColor(adminSelected)
                            )
                        }
                    )
                }
                Spacer(Modifier.weight(1f))
                HorizontalDivider(Modifier.padding(vertical = 8.dp))
                SideNavDrawerItem(
                    label = "Sign Out",
                    selected = false,
                    onClick = onSignOutClick,
                    icon = {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ExitToApp,
                            contentDescription = "Sign Out",
                            tint = navItemColor(false)
                        )
                    }
                )
                Spacer(Modifier.height(12.dp))
            }
        }
    } else {
        NavigationRail(
            containerColor = MaterialTheme.colorScheme.surface,
        ) {
            AppLogo(
                modifier = Modifier
                    .align(Alignment.CenterHorizontally)
                    .padding(top = 16.dp, bottom = 12.dp)
            )
            mainNavItems.forEach { item ->
                val selected = isTabSelected(currentRoute, item)
                SideNavRailItem(
                    label = item.label,
                    selected = selected,
                    onClick = { onTabClick(item.route) },
                    icon = {
                        Icon(
                            painter = painterResource(item.icon),
                            contentDescription = item.label,
                            tint = navItemColor(selected)
                        )
                    }
                )
            }
            HorizontalDivider(
                modifier = Modifier
                    .width(40.dp)
                    .align(Alignment.CenterHorizontally)
                    .padding(vertical = 8.dp)
            )
            SideNavRailItem(
                label = "Pay Periods",
                selected = payPeriodsSelected,
                onClick = onPayPeriodsClick,
                icon = {
                    Icon(
                        painter = painterResource(Res.drawable.baseline_article_24),
                        contentDescription = "Pay Periods",
                        tint = navItemColor(payPeriodsSelected)
                    )
                }
            )
            SideNavRailItem(
                label = "Time Off",
                selected = timeOffSelected,
                onClick = onTimeOffClick,
                icon = {
                    Icon(
                        imageVector = Icons.Default.DateRange,
                        contentDescription = "Time Off",
                        tint = navItemColor(timeOffSelected)
                    )
                }
            )
            if (isAdmin) {
                SideNavRailItem(
                    label = "Invoicing",
                    selected = invoicingSelected,
                    onClick = onInvoicingClick,
                    icon = {
                        Icon(
                            painter = painterResource(Res.drawable.baseline_receipt_long_24),
                            contentDescription = "Invoicing",
                            tint = navItemColor(invoicingSelected)
                        )
                    }
                )
                SideNavRailItem(
                    label = "Settlements",
                    selected = settlementsSelected,
                    onClick = onSettlementsClick,
                    icon = {
                        Icon(
                            painter = painterResource(Res.drawable.baseline_payments_24),
                            contentDescription = "Settlements",
                            tint = navItemColor(settlementsSelected)
                        )
                    }
                )
                SideNavRailItem(
                    label = "Admin",
                    selected = adminSelected,
                    onClick = onAdminClick,
                    icon = {
                        Icon(
                            imageVector = Icons.Default.Settings,
                            contentDescription = "Admin",
                            tint = navItemColor(adminSelected)
                        )
                    }
                )
            }
            Spacer(Modifier.weight(1f))
            SideNavRailItem(
                label = "Sign Out",
                selected = false,
                onClick = onSignOutClick,
                icon = {
                    Icon(
                        imageVector = Icons.AutoMirrored.Filled.ExitToApp,
                        contentDescription = "Sign Out",
                        tint = navItemColor(false)
                    )
                }
            )
            Spacer(Modifier.height(8.dp))
        }
    }
}

@Composable
private fun AppLogo(modifier: Modifier = Modifier) {
    Image(
        painter = painterResource(Res.drawable.app_logo),
        contentDescription = "Santa Fe Carriers logo",
        modifier = modifier
            .size(40.dp)
            .clip(RoundedCornerShape(10.dp)),
    )
}

@Composable
private fun SideNavDrawerItem(
    label: String,
    selected: Boolean,
    onClick: () -> Unit,
    icon: @Composable () -> Unit,
) {
    NavigationDrawerItem(
        modifier = Modifier.padding(vertical = 4.dp),
        label = { Text(text = label, color = navItemColor(selected)) },
        icon = icon,
        selected = selected,
        onClick = onClick,
    )
}

@Composable
private fun SideNavRailItem(
    label: String,
    selected: Boolean,
    onClick: () -> Unit,
    icon: @Composable () -> Unit,
) {
    NavigationRailItem(
        selected = selected,
        onClick = onClick,
        icon = icon,
        label = {
            Text(
                text = label,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
                color = navItemColor(selected)
            )
        }
    )
}
